/**
 * Parse the formatted USD string written by `setPricing.ts → setStartingCost`
 * into a numeric starting price used as a floor for `totalCost.minCost`.
 *
 * suffix variants (case-insensitive). `k`/`m` support is defensive; the current
 * producer writes comma-grouped USD. Strips `$`, `,`, and whitespace;
 * recognises trailing lowercase `k` / `m` suffix (multiply by 1000 /
 * 1_000_000 respectively) and parses the remainder with `parseFloat`.
 *
 * Returns `null` for null/undefined/empty input or unparseable strings.
 *
 * Examples:
 * - `'$15,000'` → `15000`
 * - `'$15k'`    → `15000`
 * - `'$2.5m'`   → `2_500_000`
 * - `'15000'`   → `15000`
 * - `'$abc'`    → `null`
 */
export const parseStartingCostToNumber = (
	value: string | null | undefined,
): number | null => {
	if (value === null || value === undefined) return null;
	const trimmed = value.trim();
	if (trimmed === '') return null;

	const normalized = trimmed.replace(/[$,\s]/g, '').toLowerCase();
	if (normalized === '') return null;

	const match = normalized.match(/^([0-9]+(?:\.[0-9]+)?)([km]?)$/);
	if (!match) return null;

	const numeric = Number.parseFloat(match[1]);
	if (!Number.isFinite(numeric)) return null;

	const suffix = match[2];
	const multiplier = suffix === 'k' ? 1_000 : suffix === 'm' ? 1_000_000 : 1;

	return numeric * multiplier;
};

/**
 * Apply the starting-cost floor to a computed `totalCost.minCost`.
 *
 * When `startingCost` is a finite number, returns `max(minCost, startingCost)`.
 * Otherwise returns `minCost` unchanged. NaN and Infinity are treated as
 * missing — the floor is skipped, not propagated, so a downstream caller
 * never sees an out-of-range value derived from a bad input.
 */
export const applyStartingCostFloor = (
	minCost: number,
	startingCost: number | null | undefined,
): number => {
	if (startingCost === null || startingCost === undefined) return minCost;
	if (!Number.isFinite(startingCost)) return minCost;
	return Math.max(minCost, startingCost);
};
